/**
 * Shared formatting utilities for betting market display names.
 * Converts raw database keys (e.g. "over_2_5", "btts_yes") into
 * human-readable labels for the UI.
 */

export const getSelectionLabel = (selection, homeTeam, awayTeam) => {
  const home = homeTeam || 'Home';
  const away = awayTeam || 'Away';

  const formatGoals = (str) => str.replace(/_/g, '.');

  const selectionMap = {
    'home': `${home} Win`,
    'away': `${away} Win`,
    'draw': 'Draw',
    'btts_yes': 'BTTS Yes',
    'btts_no': 'BTTS No',
    'double_chance_1x': `1X (${home} or Draw)`,
    'double_chance_x2': `X2 (Draw or ${away})`,
    'double_chance_12': `12 (${home} or ${away})`
  };

  if (!selection) return selection;

  // Handle BTTS variations
  if (selection === 'btts_yes' || selection === 'btts yes') return 'BTTS Yes';
  if (selection === 'btts_no' || selection === 'btts no') return 'BTTS No';

  // Handle Over/Under markets dynamically
  if (selection.startsWith('over_')) {
    const value = formatGoals(selection.replace('over_', ''));
    return `Over ${value} Goals`;
  }
  if (selection.startsWith('under_')) {
    const value = formatGoals(selection.replace('under_', ''));
    return `Under ${value} Goals`;
  }

  // Handle Double Chance variations
  if (selection.includes('double_chance')) {
    if (selection.includes('1x') || selection.includes('1X')) return `1X (${home} or Draw)`;
    if (selection.includes('x2') || selection.includes('X2')) return `X2 (Draw or ${away})`;
    if (selection.includes('12')) return `12 (${home} or ${away})`;
  }

  // === HOME TEAM MARKETS ===
  if (selection.startsWith('home_total_')) {
    const rest = selection.replace('home_total_', '');
    if (rest.startsWith('over_')) return `${home} Over ${formatGoals(rest.replace('over_', ''))} Goals`;
    if (rest.startsWith('under_')) return `${home} Under ${formatGoals(rest.replace('under_', ''))} Goals`;
  }
  if (selection.startsWith('home_goals_')) {
    const rest = selection.replace('home_goals_', '');
    if (rest.startsWith('over_')) return `${home} Over ${formatGoals(rest.replace('over_', ''))} Goals`;
    if (rest.startsWith('under_')) return `${home} Under ${formatGoals(rest.replace('under_', ''))} Goals`;
  }
  if (selection.startsWith('home_clean_sheet_')) {
    const val = selection.replace('home_clean_sheet_', '');
    return `${home} Clean Sheet ${val === 'yes' ? 'Yes' : 'No'}`;
  }
  if (selection.startsWith('clean_sheet_home_')) {
    const val = selection.replace('clean_sheet_home_', '');
    return `${home} Clean Sheet ${val === 'yes' ? 'Yes' : 'No'}`;
  }
  if (selection.startsWith('home_1h_')) {
    const rest = selection.replace('home_1h_', '');
    if (rest.startsWith('over_')) return `${home} 1H Over ${formatGoals(rest.replace('over_', ''))}`;
    if (rest.startsWith('under_')) return `${home} 1H Under ${formatGoals(rest.replace('under_', ''))}`;
  }
  if (selection.startsWith('home_2h_')) {
    const rest = selection.replace('home_2h_', '');
    if (rest.startsWith('over_')) return `${home} 2H Over ${formatGoals(rest.replace('over_', ''))}`;
    if (rest.startsWith('under_')) return `${home} 2H Under ${formatGoals(rest.replace('under_', ''))}`;
  }

  // === AWAY TEAM MARKETS ===
  if (selection.startsWith('away_total_')) {
    const rest = selection.replace('away_total_', '');
    if (rest.startsWith('over_')) return `${away} Over ${formatGoals(rest.replace('over_', ''))} Goals`;
    if (rest.startsWith('under_')) return `${away} Under ${formatGoals(rest.replace('under_', ''))} Goals`;
  }
  if (selection.startsWith('away_goals_')) {
    const rest = selection.replace('away_goals_', '');
    if (rest.startsWith('over_')) return `${away} Over ${formatGoals(rest.replace('over_', ''))} Goals`;
    if (rest.startsWith('under_')) return `${away} Under ${formatGoals(rest.replace('under_', ''))} Goals`;
  }
  if (selection.startsWith('away_clean_sheet_')) {
    const val = selection.replace('away_clean_sheet_', '');
    return `${away} Clean Sheet ${val === 'yes' ? 'Yes' : 'No'}`;
  }
  if (selection.startsWith('clean_sheet_away_')) {
    const val = selection.replace('clean_sheet_away_', '');
    return `${away} Clean Sheet ${val === 'yes' ? 'Yes' : 'No'}`;
  }
  if (selection.startsWith('away_1h_')) {
    const rest = selection.replace('away_1h_', '');
    if (rest.startsWith('over_')) return `${away} 1H Over ${formatGoals(rest.replace('over_', ''))}`;
    if (rest.startsWith('under_')) return `${away} 1H Under ${formatGoals(rest.replace('under_', ''))}`;
  }
  if (selection.startsWith('away_2h_')) {
    const rest = selection.replace('away_2h_', '');
    if (rest.startsWith('over_')) return `${away} 2H Over ${formatGoals(rest.replace('over_', ''))}`;
    if (rest.startsWith('under_')) return `${away} 2H Under ${formatGoals(rest.replace('under_', ''))}`;
  }

  // === FIRST HALF MARKETS ===
  if (selection.startsWith('first_half_')) {
    const rest = selection.replace('first_half_', '');
    if (rest.startsWith('result_')) {
      const result = rest.replace('result_', '');
      if (result === 'home') return `1H ${home} Win`;
      if (result === 'away') return `1H ${away} Win`;
      if (result === 'draw') return '1H Draw';
    }
    if (rest.startsWith('dc_')) {
      const dc = rest.replace('dc_', '');
      if (dc === '1x') return `1H 1X (${home} or Draw)`;
      if (dc === 'x2') return `1H X2 (Draw or ${away})`;
      if (dc === '12') return `1H 12 (${home} or ${away})`;
    }
    if (rest === 'btts_yes') return '1H BTTS Yes';
    if (rest === 'btts_no') return '1H BTTS No';
    if (rest.startsWith('over_')) return `1H Over ${formatGoals(rest.replace('over_', ''))} Goals`;
    if (rest.startsWith('under_')) return `1H Under ${formatGoals(rest.replace('under_', ''))} Goals`;
  }

  // === SECOND HALF MARKETS ===
  if (selection.startsWith('second_half_')) {
    const rest = selection.replace('second_half_', '');
    if (rest === 'btts_yes') return '2H BTTS Yes';
    if (rest === 'btts_no') return '2H BTTS No';
    if (rest.startsWith('over_')) return `2H Over ${formatGoals(rest.replace('over_', ''))} Goals`;
    if (rest.startsWith('under_')) return `2H Under ${formatGoals(rest.replace('under_', ''))} Goals`;
  }

  // === COMBINED MARKETS ===
  if (selection.startsWith('result_btts_')) {
    const parts = selection.replace('result_btts_', '').split('_');
    const result = parts[0];
    const btts = parts[1];
    const resultLabel = result === 'home' ? `${home} Win` : result === 'away' ? `${away} Win` : 'Draw';
    return `${resultLabel} & BTTS ${btts === 'yes' ? 'Yes' : 'No'}`;
  }
  if (selection.startsWith('result_goals_')) {
    const parts = selection.replace('result_goals_', '').split('_');
    const result = parts[0];
    const ou = parts[1];
    const goals = parts.slice(2).join('.');
    const resultLabel = result === 'home' ? `${home} Win` : result === 'away' ? `${away} Win` : 'Draw';
    return `${resultLabel} & ${ou === 'over' ? 'Over' : 'Under'} ${goals} Goals`;
  }
  if (selection.startsWith('goals_btts_')) {
    const parts = selection.replace('goals_btts_', '').split('_');
    const ou = parts[0];
    const goals = parts[1] + '.' + parts[2];
    const btts = parts[3];
    return `${ou === 'over' ? 'Over' : 'Under'} ${goals} Goals & BTTS ${btts === 'yes' ? 'Yes' : 'No'}`;
  }

  // === WIN TO NIL ===
  if (selection.startsWith('win_to_nil_')) {
    const team = selection.replace('win_to_nil_', '');
    if (team === 'home') return `${home} Win to Nil`;
    if (team === 'away') return `${away} Win to Nil`;
    return `${team.charAt(0).toUpperCase() + team.slice(1)} Win to Nil`;
  }

  // === EXACT GOALS ===
  if (selection.startsWith('exact_goals_')) {
    const val = selection.replace('exact_goals_', '').replace('plus', '+');
    return `Exactly ${val} Goals`;
  }

  // === ODD/EVEN GOALS ===
  if (selection.startsWith('odd_even_')) {
    const val = selection.replace('odd_even_', '');
    return `${val.charAt(0).toUpperCase() + val.slice(1)} Total Goals`;
  }

  // === ASIAN HANDICAP ===
  if (selection.startsWith('asian_handicap_')) {
    const rest = selection.replace('asian_handicap_', '');
    if (rest.startsWith('1h_')) {
      const hc = rest.replace('1h_', '').replace('plus', '+').replace('minus', '-').replace(/_/g, '.');
      return `1H Asian Handicap ${hc}`;
    }
    const hc = rest.replace('plus', '+').replace('minus', '-').replace(/_/g, '.');
    return `Asian Handicap ${hc}`;
  }

  // === DRAW NO BET ===
  if (selection.startsWith('draw_no_bet_')) {
    const team = selection.replace('draw_no_bet_', '');
    if (team === 'home') return `DNB ${home}`;
    if (team === 'away') return `DNB ${away}`;
  }

  return selectionMap[selection] || selection;
};
